import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { MemberContextRoot } from './types/MemberContextRoot';
import { useNcassAuth0 } from '../../../hooks/useNcassAuth0';
import ApiResource from '../../ApiResource';
import { useMembershipImpersonationContext } from '../../MembershipImpersonation/MembershipImpersonationContext';
import { appMenuController } from '../../AppMenu/appMenuController';
import { StaleAwareContext } from '../useStaleAwareDataContext';
import { defaultMemberContextRoot } from './defaultMemberContextRoot';

export interface MemberContextType extends StaleAwareContext<MemberContextRoot>{  
  data: MemberContextRoot | null;
  reload: () => void;
  lastLoaded: Date | null;
  isLoading: boolean;
}

const MemberContext = createContext<MemberContextType | undefined>(undefined);

export const MemberProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useNcassAuth0();
  const { isImpersonatingMember, accountIdOverride, impersonationOverride, setImpersonationOverride } = useMembershipImpersonationContext();
  const [memberData, setMemberData] = useState<MemberContextRoot>(defaultMemberContextRoot);
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!memberData) return;
    if (memberData.latestMembership.unit === 'Fixed') {
        appMenuController.hide(['My WorkOpps', 'Work Opportunities']);
    } else {
      appMenuController.show( ['My WorkOpps', 'Work Opportunities']);
    }

    return () => {
      
      appMenuController.reset();
    };
  }, [memberData]);

  const reload = useCallback(() => {
    setReloadKey(prev => prev + 1);
    setLastLoaded(new Date());
    setIsLoading(true);
  }, []);

  const onContextReceived = (memberContextRoot: MemberContextRoot) => {
    setLastLoaded(new Date());
    setIsLoading(false);

    const isAccountManager = user?.roles?.includes("accountManager");
    if (isAccountManager && !isImpersonatingMember) {
      setImpersonationOverride({...impersonationOverride, accountManagerPermissions: memberContextRoot.permissions, memberPermissions: memberContextRoot.permissions});
    } else {
      setImpersonationOverride({...impersonationOverride, memberPermissions: memberContextRoot.permissions});
    }
  }

  const accountId = isImpersonatingMember ? accountIdOverride : user.accountId;
  return (
    <ApiResource<MemberContextRoot, MemberContextRoot>
      key={`${accountId}-${reloadKey}`}
      resource={`/context/${accountId}/member`}
      managedDataState={[memberData, setMemberData]}
      disableRerenderLoading={true}
      onDataReceived={onContextReceived}
      defaultData={defaultMemberContextRoot}
    >
      {(data) => (
        <MemberContext.Provider value={{ data, reload, lastLoaded, isLoading }}>
          {children}
        </MemberContext.Provider>
      )}
    </ApiResource>
  );
};

export const useMemberContext = (): MemberContextType => {
  const context = useContext(MemberContext);
  if (!context) {
    throw new Error('useMemberContext must be used within a MemberProvider');
  }

  return context;
};
